<template>
  <section v-show="active" class="property-map-panel">
    <div class="property-map-header">
      <div>
        <h3><b-icon-map-fill /> Localização no mapa</h3>
        <p>Clique no mapa para marcar a propriedade e preencher o endereço.</p>
      </div>
    </div>

    <div class="property-map-content">
      <div ref="map" class="property-map-canvas"></div>
      <button
        v-if="map"
        type="button"
        class="map-layer-toggle"
        :aria-pressed="isSatellite"
        :title="isSatellite ? 'Exibir mapa padrão' : 'Exibir mapa por satélite'"
        @click="toggleMapLayer"
      >
        {{ isSatellite ? "Mapa" : "Satélite" }}
      </button>
      <div v-if="isLoading" class="property-map-feedback">
        <b-spinner small variant="success" />
        <span>{{ loadingMessage }}</span>
      </div>
      <div v-if="errorMessage" class="property-map-error">
        <b-icon-exclamation-triangle-fill /> {{ errorMessage }}
      </div>
    </div>
  </section>
</template>

<script>
import axios from "axios";

const MAPS_SCRIPT_ID = "winfit-google-maps-sdk";
const DEFAULT_CENTER = { lat: -14.235, lng: -51.9253 };
let mapsLoaderPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (mapsLoaderPromise) return mapsLoaderPromise;

  mapsLoaderPromise = new Promise((resolve, reject) => {
    const apiKey = process.env.VUE_APP_KEY_API_GOOGLE_MAPS;
    const geolocationBaseUrl = process.env.VUE_APP_KEY_BASE_URL_GEOLOCATION;

    if (!apiKey || !geolocationBaseUrl) {
      reject(new Error("Configuração do Google Maps não encontrada."));
      return;
    }

    const existingScript = document.getElementById(MAPS_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener(
        "load",
        () => resolve(window.google.maps),
        { once: true }
      );
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const googleOrigin = new URL(geolocationBaseUrl).origin;
    const script = document.createElement("script");
    script.id = MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `${googleOrigin}/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&language=pt-BR`;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () =>
      reject(new Error("Não foi possível carregar o Google Maps."));
    document.head.appendChild(script);
  }).catch((error) => {
    mapsLoaderPromise = null;
    throw error;
  });

  return mapsLoaderPromise;
}

export default {
  name: "PropertyMap",
  props: {
    active: { type: Boolean, default: false },
    latitude: { type: [String, Number], default: null },
    longitude: { type: [String, Number], default: null },
    markerCoordinates: { type: Object, default: null },
  },
  data() {
    return {
      map: null,
      marker: null,
      mapClickListener: null,
      isLoading: false,
      loadingMessage: "Carregando mapa...",
      errorMessage: "",
      hasRequestedInitialLocation: false,
      isSatellite: false,
    };
  },
  computed: {
    selectedCoordinates() {
      if (
        this.latitude === null ||
        this.latitude === "" ||
        this.longitude === null ||
        this.longitude === ""
      ) {
        return null;
      }
      const lat = Number(this.latitude);
      const lng = Number(this.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.activateMap();
      },
    },
    selectedCoordinates(coordinates) {
      if (coordinates && this.map) this.setMarker(coordinates, true);
    },
    markerCoordinates(coordinates) {
      if (coordinates && this.map) this.setMarker(coordinates, true);
    },
  },
  methods: {
    async activateMap() {
      await this.$nextTick();

      if (this.map) {
        window.google.maps.event.trigger(this.map, "resize");
        const center = this.marker?.getPosition() || this.map.getCenter();
        if (center) this.map.setCenter(center);
        return;
      }

      await this.initializeMap();
    },
    async initializeMap() {
      if (!this.$refs.map || this.isLoading) return;

      this.isLoading = true;
      this.loadingMessage = "Obtendo sua localização...";
      this.errorMessage = "";

      try {
        const [, currentPosition] = await Promise.all([
          loadGoogleMaps(),
          this.getCurrentPosition(),
        ]);

        if (!this.$refs.map || this._isBeingDestroyed) return;

        this.map = new window.google.maps.Map(this.$refs.map, {
          center: currentPosition,
          zoom: 16,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        this.mapClickListener = this.map.addListener("click", ({ latLng }) => {
          this.selectLocation({ lat: latLng.lat(), lng: latLng.lng() });
        });

        if (this.selectedCoordinates)
          this.setMarker(this.selectedCoordinates, true);
      } catch (error) {
        this.errorMessage =
          error.message || "Não foi possível inicializar o mapa.";
      } finally {
        this.isLoading = false;
      }
    },
    getCurrentPosition() {
      if (this.hasRequestedInitialLocation || !navigator.geolocation) {
        return Promise.resolve(DEFAULT_CENTER);
      }

      this.hasRequestedInitialLocation = true;
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) =>
            resolve({ lat: coords.latitude, lng: coords.longitude }),
          () => resolve(DEFAULT_CENTER),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      });
    },
    async selectLocation(coordinates) {
      this.setMarker(coordinates, true);
      this.isLoading = true;
      this.loadingMessage = "Buscando endereço...";
      this.errorMessage = "";

      try {
        const { data } = await axios.get(
          process.env.VUE_APP_KEY_BASE_URL_GEOLOCATION,
          {
            params: {
              latlng: `${coordinates.lat},${coordinates.lng}`,
              key: process.env.VUE_APP_KEY_API_GOOGLE_MAPS,
              language: "pt-BR",
            },
          }
        );

        const result = data?.results?.[0];
        if (!result)
          throw new Error("Nenhum endereço encontrado para este ponto.");

        this.$emit("location-selected", this.parseAddress(result, coordinates));
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error_message ||
          error.message ||
          "Falha ao buscar o endereço.";
      } finally {
        this.isLoading = false;
      }
    },
    setMarker(coordinates, centerMap = false) {
      if (!this.map) return;

      if (!this.marker) {
        this.marker = new window.google.maps.Marker({
          map: this.map,
          position: coordinates,
        });
      } else {
        this.marker.setPosition(coordinates);
      }

      if (centerMap) this.map.panTo(coordinates);
    },
    toggleMapLayer() {
      if (!this.map) return;

      this.isSatellite = !this.isSatellite;
      this.map.setMapTypeId(
        this.isSatellite
          ? window.google.maps.MapTypeId.HYBRID
          : window.google.maps.MapTypeId.ROADMAP
      );
    },
    parseAddress(result, coordinates) {
      const components = result.address_components || [];
      const component = (type) =>
        components.find((item) => item.types.includes(type));
      const city =
        component("administrative_area_level_2") || component("locality");
      const district =
        component("sublocality_level_1") ||
        component("sublocality") ||
        component("neighborhood");

      return {
        cep: component("postal_code")?.long_name || "",
        endereco: component("route")?.long_name || "",
        bairro: district?.long_name || "",
        cidade: city?.long_name || "",
        uf: component("administrative_area_level_1")?.short_name || "",
        numero: component("street_number")?.long_name || "",
        latitude: coordinates.lat.toFixed(7),
        longitude: coordinates.lng.toFixed(7),
      };
    },
    async geocodeAddress(addressString) {
      if (!addressString || !window.google?.maps) return;

      this.isLoading = true;
      this.loadingMessage = "Buscando localização no mapa...";
      this.errorMessage = "";

      try {
        const geocoder = new window.google.maps.Geocoder();
        const { results } = await geocoder.geocode({
          address: addressString,
          region: "BR",
        });

        const result = results?.[0];
        if (!result) throw new Error("Localização não encontrada.");

        const coordinates = {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
        };

        this.setMarker(coordinates, true);
        this.$emit("location-selected", this.parseAddress(result, coordinates));
      } catch (error) {
        this.errorMessage =
          error.message || "Falha ao buscar localização no mapa.";
      } finally {
        this.isLoading = false;
      }
    },
  },
  beforeDestroy() {
    if (this.mapClickListener && window.google?.maps) {
      window.google.maps.event.removeListener(this.mapClickListener);
    }
    if (this.marker) this.marker.setMap(null);
    this.mapClickListener = null;
    this.marker = null;
    this.map = null;
  },
};
</script>

<style scoped>
.property-map-panel {
  height: 100%;
  min-height: 620px;
  padding: 6px;
  border: 1px solid #e2e8e6;
  border-radius: 5px;
  background: #fff;
}

.property-map-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 5px;
  margin: 0 5px;
}

.property-map-header h3 {
  margin: 0;
  color: #006256;
  font-size: 15px;
  font-weight: 700;
}
.property-map-header p {
  margin: 4px 0 0;
  color: #6c757d;
  font-size: 11px;
}
.property-map-content {
  position: relative;
  height: calc(100% - 58px);
  min-height: 540px;
}
.property-map-canvas {
  width: 100%;
  height: 100%;
  min-height: 540px;
  border-radius: 8px;
  background: #eef2f1;
}
.map-layer-toggle {
  position: absolute;
  z-index: 1;
  top: 10px;
  left: 10px;
  padding: 7px 12px;
  border: 1px solid #d7e2df;
  border-radius: 4px;
  background: #fff;
  color: #006256;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}
.map-layer-toggle:hover,
.map-layer-toggle:focus {
  border-color: #006256;
  outline: none;
}
.property-map-feedback,
.property-map-error {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.property-map-feedback {
  background: rgba(255, 255, 255, 0.95);
  color: #006256;
}
.property-map-error {
  background: #fff3f3;
  color: #a01818;
}

@media (max-width: 991.98px) {
  .property-map-panel {
    min-height: 480px;
  }
  .property-map-content,
  .property-map-canvas {
    min-height: 400px;
  }
}
</style>
